/**
 * Client-local onboarding flags. All keys use the shared `hbs:` prefix so
 * `clearAllLocalData()` wipes them on logout. There is no server-side user
 * prefs store, so onboarding progress is per-browser by design.
 *
 * Completion of most steps is *derived* from real safe state (items, orgs,
 * recovery contacts) elsewhere; these flags cover only the bits we can't
 * observe: that onboarding is active for this (new) account, that the user
 * dismissed the checklist, and that they confirmed installing the extension.
 */

const KEYS = {
  active: 'hbs:onboarding:active',
  welcomeSeen: 'hbs:onboarding:welcomeSeen',
  dismissed: 'hbs:onboarding:dismissed',
  extensionInstalled: 'hbs:onboarding:extensionInstalled',
} as const;

function read(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function write(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    /* private mode / storage disabled — onboarding just won't persist */
  }
}

/** Marks onboarding active for a freshly-registered account. */
export function startOnboarding(): void {
  write(KEYS.active);
}

export const isOnboardingActive = (): boolean => read(KEYS.active);

export const hasSeenWelcome = (): boolean => read(KEYS.welcomeSeen);
export const markWelcomeSeen = (): void => write(KEYS.welcomeSeen);

export const isChecklistDismissed = (): boolean => read(KEYS.dismissed);
export const dismissChecklist = (): void => write(KEYS.dismissed);

export const isExtensionMarkedInstalled = (): boolean => read(KEYS.extensionInstalled);
export const markExtensionInstalled = (): void => write(KEYS.extensionInstalled);
